import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, rupiah, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, voucher, clear, clearVoucher } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ shipping_address: "", courier: "", notes: "", delivery_method: "kirim", payment_method: "transfer" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/masuk?next=/checkout");
    if (user && user.address && !form.shipping_address) setForm((f) => ({ ...f, shipping_address: user.address }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return toast.error("Keranjang kosong");
    setLoading(true);
    try {
      await API.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, variant_label: i.variant_label, unit: i.unit, qty: i.qty })),
        shipping_address: form.delivery_method === "ambil_sendiri" ? form.shipping_address || "Ambil sendiri di toko" : form.shipping_address,
        courier: form.delivery_method === "ambil_sendiri" ? "" : form.courier,
        notes: form.notes,
        voucher_code: voucher?.code || "",
        voucher_id: voucher?.vid || "",
        delivery_method: form.delivery_method,
        payment_method: form.payment_method,
      });
      clear();
      clearVoucher();
      toast.success(form.delivery_method === "ambil_sendiri" ? "Pesanan dibuat! Silakan ambil barang di toko setelah pembayaran dikonfirmasi." : "Pesanan dibuat! Pemilik akan menginformasikan ongkir.");
      navigate("/pesanan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-10" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-display text-3xl tracking-tight mb-8">Checkout</h1>
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 space-y-5">
            <h2 className="font-bold">Informasi Pengiriman</h2>
            <div>
              <label className={labelCls}>Metode Pengiriman</label>
              <div className="grid grid-cols-2 gap-3" data-testid="delivery-method-group">
                {[
                  { v: "kirim", l: "Pakai Jasa Pengiriman", d: "Dikirim via kurir/ekspedisi — ongkir dihitung pemilik" },
                  { v: "ambil_sendiri", l: "Ambil Sendiri di Toko", d: "Tanpa ongkir — ambil langsung di toko" },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setForm({ ...form, delivery_method: m.v })}
                    data-testid={`delivery-${m.v}`}
                    className={`p-4 rounded-md border text-left transition-colors ${form.delivery_method === m.v ? "border-[#C9A227] bg-amber-50" : "border-slate-300 hover:border-[#0B1F3A]"}`}
                  >
                    <p className="font-bold text-sm">{m.l}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.d}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Metode Pembayaran</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="payment-method-group">
                {[
                  { v: "cash", l: "Cash / Tunai", d: "Bayar tunai saat barang diterima / diambil" },
                  { v: "transfer", l: "Transfer Bank", d: "Transfer ke rekening resmi, lalu unggah bukti" },
                  { v: "qris", l: "QRIS", d: "Scan kode QRIS, lalu unggah bukti bayar" },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setForm({ ...form, payment_method: m.v })}
                    data-testid={`payment-${m.v}`}
                    className={`p-4 rounded-md border text-left transition-colors ${form.payment_method === m.v ? "border-[#C9A227] bg-amber-50" : "border-slate-300 hover:border-[#0B1F3A]"}`}
                  >
                    <p className="font-bold text-sm">{m.l}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.d}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Alamat Pengiriman Lengkap {form.delivery_method === "ambil_sendiri" ? "(opsional)" : "*"}</label>
              <textarea required={form.delivery_method === "kirim"} rows={3} value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} data-testid="checkout-address" className={inputCls} placeholder={form.delivery_method === "ambil_sendiri" ? "Tidak wajib — barang diambil sendiri di toko" : "Jalan, nomor, kecamatan, kota, kode pos"} />
            </div>
            {form.delivery_method === "kirim" && (
              <div>
                <label className={labelCls}>Kurir / Ekspedisi Pilihan (opsional)</label>
                <input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} data-testid="checkout-courier" className={inputCls} placeholder="Contoh: JNE, J&T, Cargo" />
              </div>
            )}
            <div>
              <label className={labelCls}>Catatan (opsional)</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" className={inputCls} placeholder="Catatan untuk pemilik" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-600">
              <p className="font-bold text-slate-800 mb-1">Cara Pembayaran</p>
              <p>Pilih metode pembayaran: <strong>Cash/Tunai</strong>, <strong>Transfer Bank</strong>, atau <strong>QRIS</strong>. Setelah pesanan dibuat, pemilik mengonfirmasi total akhir — instruksi rekening / kode QRIS dan tombol unggah bukti tersedia di halaman Pesanan Saya. Jika pembayaran tempo disepakati, pemilik yang akan mengatur jatuh tempo pada pesanan Anda.</p>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-6 h-fit" data-testid="checkout-summary">
            <h2 className="font-bold mb-4">Pesanan Anda</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {items.map((i) => (
                <div key={`${i.product_id}-${i.variant_label}-${i.unit}`} className="flex justify-between text-sm">
                  <span className="text-slate-600 pr-3">{i.name} ({i.variant_label}) × {i.qty}</span>
                  <span className="font-semibold whitespace-nowrap">{rupiah(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm py-3 mt-3 border-t border-slate-200">
              <span className="text-slate-500">Subtotal</span>
              <span className="font-bold" data-testid="checkout-subtotal">{rupiah(subtotal)}</span>
            </div>
            {voucher && (
              <div className="flex justify-between text-sm pb-2" data-testid="checkout-voucher-row">
                <span className="text-emerald-700 font-semibold">Voucher {voucher.code}</span>
                <span className="font-bold text-emerald-700">- {rupiah(voucher.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm pb-3 border-b border-slate-200">
              <span className="text-slate-500">Ongkir</span>
              <span className="text-xs text-slate-400" data-testid="checkout-ongkir-note">{form.delivery_method === "ambil_sendiri" ? "Gratis — Ambil Sendiri" : "Menyusul"}</span>
            </div>
            <div className="flex justify-between text-sm py-3 border-b border-slate-200 font-bold">
              <span>Total Sementara</span>
              <span data-testid="checkout-total">{rupiah(Math.max(0, subtotal - (voucher?.discount || 0)))}</span>
            </div>
            <button
              type="submit"
              disabled={loading || !items.length}
              data-testid="place-order-button"
              className="mt-5 w-full px-6 py-3.5 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors disabled:opacity-60"
            >
              {loading ? "Memproses..." : "Buat Pesanan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
